import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Input } from './ui/input';
import { PencilSimple, Trash, LinkBreak, Check, X } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const L = {
  el: {
    title: 'Πληρωμές Τιμολογίου', date: 'Ημερομηνία', amount: 'Ποσό', method: 'Τρόπος', reference: 'Reference / ID',
    origin: 'Προέλευση', notes: 'Σημειώσεις', actions: 'Ενέργειες', total: 'Σύνολο Τιμολογίου', paid: 'Σύνολο Πληρωμών',
    balance: 'Υπόλοιπο', status: 'Status', none: 'Δεν υπάρχουν ενεργές πληρωμές για αυτό το τιμολόγιο.',
    internal_cash: 'Internal Cash', bank: 'Τράπεζα (Reference Only)', legacy: 'Παλιό σύστημα',
    edit: 'Επεξεργασία', del: 'Διαγραφή', unlink: 'Αποσύνδεση', save: 'Αποθήκευση', cancel: 'Ακύρωση',
    confirmDel: 'Διαγραφή πληρωμής', confirmUnlink: 'Αποσύνδεση τραπεζικής κίνησης',
    delDesc: 'Η πληρωμή θα αφαιρεθεί από το τιμολόγιο και θα επανυπολογιστούν το εξοφλημένο ποσό, το υπόλοιπο και το status. Η ενέργεια καταγράφεται στο Audit Log.',
    unlinkDesc: 'Θα αφαιρεθεί μόνο η σύνδεση με αυτό το τιμολόγιο. Η τραπεζική κίνηση ΔΕΝ διαγράφεται.',
    multi: 'Μοιρασμένη σε πολλά τιμολόγια — επηρεάζεται μόνο αυτό το τιμολόγιο', deleteInvoice: 'Διαγραφή Τιμολογίου',
    deleteInvoiceHint: 'Αφαιρέστε πρώτα όλες τις πληρωμές για να διαγράψετε το τιμολόγιο.', audit: 'Ιστορικό αλλαγών (Audit Log)',
    saved: 'Η πληρωμή ενημερώθηκε', removed: 'Η πληρωμή αφαιρέθηκε', of: 'από',
  },
  en: {
    title: 'Invoice Payments', date: 'Date', amount: 'Amount', method: 'Method', reference: 'Reference / ID',
    origin: 'Source', notes: 'Notes', actions: 'Actions', total: 'Invoice Total', paid: 'Total Paid',
    balance: 'Balance', status: 'Status', none: 'No active payments for this invoice.',
    internal_cash: 'Internal Cash', bank: 'Bank (Reference Only)', legacy: 'Legacy system',
    edit: 'Edit', del: 'Delete', unlink: 'Unlink', save: 'Save', cancel: 'Cancel',
    confirmDel: 'Delete payment', confirmUnlink: 'Unlink bank transaction',
    delDesc: 'The payment will be removed from this invoice and paid amount, balance and status will be recalculated. The action is recorded in the Audit Log.',
    unlinkDesc: 'Only the link to this invoice is removed. The bank transaction is NOT deleted.',
    multi: 'Split across several invoices — only this invoice is affected', deleteInvoice: 'Delete Invoice',
    deleteInvoiceHint: 'Remove all payments first to delete the invoice.', audit: 'Change history (Audit Log)',
    saved: 'Payment updated', removed: 'Payment removed', of: 'of',
  },
};

const money = (v) => `€${(Number(v) || 0).toFixed(2)}`;
const fmtD = (d) => (d ? d.slice(0, 10).split('-').reverse().join('/') : '-');

const EditRow = ({ row, s, onSave, onCancel }) => {
  const [f, setF] = useState({ date: (row.date || '').slice(0, 10), amount: row.amount, method: row.method, reference: row.reference, notes: row.notes });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <tr className="bg-amber-50" data-testid={`inv-pay-edit-row-${row.row_id}`}>
      <td className="p-1"><Input type="date" value={f.date} onChange={set('date')} className="h-8 rounded-none text-xs" data-testid="inv-pay-edit-date" /></td>
      <td className="p-1"><Input type="number" step="0.01" value={f.amount} onChange={set('amount')} className="h-8 rounded-none text-xs w-24" data-testid="inv-pay-edit-amount" /></td>
      <td className="p-1">
        <select value={f.method} onChange={set('method')} className="h-8 border border-zinc-300 text-xs px-1" data-testid="inv-pay-edit-method">
          {['cash', 'bank', 'transfer', 'card', 'cheque'].map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </td>
      <td className="p-1"><Input value={f.reference} onChange={set('reference')} className="h-8 rounded-none text-xs" data-testid="inv-pay-edit-reference" /></td>
      <td className="p-1 text-xs text-zinc-500">{s[row.origin]}</td>
      <td className="p-1"><Input value={f.notes} onChange={set('notes')} className="h-8 rounded-none text-xs" data-testid="inv-pay-edit-notes" /></td>
      <td className="p-1 whitespace-nowrap">
        <button onClick={() => onSave(f)} className="p-1.5 text-emerald-700 hover:bg-emerald-100" title={s.save} data-testid="inv-pay-edit-save"><Check size={14} /></button>
        <button onClick={onCancel} className="p-1.5 text-zinc-600 hover:bg-zinc-100" title={s.cancel} data-testid="inv-pay-edit-cancel"><X size={14} /></button>
      </td>
    </tr>
  );
};

const PayRow = ({ row, s, isAdmin, onEdit, onRemove }) => (
  <tr className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`inv-pay-row-${row.row_id}`}>
    <td className="px-2 py-2 text-xs font-mono">{fmtD(row.date)}</td>
    <td className="px-2 py-2 text-xs font-mono font-semibold" data-testid={`inv-pay-amount-${row.row_id}`}>
      {money(row.amount)}
      {row.multi_invoice && <div className="text-[10px] font-normal text-amber-700" title={s.multi}>{s.of} {money(row.payment_total)}</div>}
    </td>
    <td className="px-2 py-2 text-xs capitalize">{row.method || '-'}</td>
    <td className="px-2 py-2 text-xs">{row.payment_no || row.reference || '-'}{row.payment_no && row.reference && row.reference !== row.payment_no ? <div className="text-[10px] text-zinc-500">{row.reference}</div> : null}</td>
    <td className="px-2 py-2 text-xs"><span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider border ${row.origin === 'bank' ? 'border-sky-300 text-sky-700 bg-sky-50' : row.origin === 'internal_cash' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-zinc-300 text-zinc-600 bg-zinc-50'}`} data-testid={`inv-pay-origin-${row.row_id}`}>{s[row.origin]}</span></td>
    <td className="px-2 py-2 text-xs text-zinc-600 max-w-[180px] truncate" title={row.notes}>{row.notes || '-'}</td>
    <td className="px-2 py-2 whitespace-nowrap">
      {row.can_edit && <button onClick={onEdit} className="p-1.5 text-zinc-700 hover:bg-zinc-100" title={s.edit} data-testid={`inv-pay-edit-${row.row_id}`}><PencilSimple size={14} /></button>}
      {isAdmin && (row.action === 'unlink'
        ? <button onClick={onRemove} className="p-1.5 text-sky-700 hover:bg-sky-50" title={s.unlink} data-testid={`inv-pay-unlink-${row.row_id}`}><LinkBreak size={14} /></button>
        : <button onClick={onRemove} className="p-1.5 text-red-600 hover:bg-red-50" title={s.del} data-testid={`inv-pay-delete-${row.row_id}`}><Trash size={14} /></button>)}
    </td>
  </tr>
);

const Summary = ({ inv, s }) => (
  <div className="grid grid-cols-4 gap-px bg-zinc-200 border border-zinc-200 mb-4" data-testid="inv-pay-summary">
    {[['total', inv.total], ['paid', inv.paid_amount], ['balance', inv.balance]].map(([k, v]) => (
      <div key={k} className="bg-white px-3 py-2">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{s[k]}</div>
        <div className={`font-mono text-sm font-bold ${k === 'balance' && v > 0.01 ? 'text-amber-700' : 'text-zinc-900'}`} data-testid={`inv-pay-summary-${k}`}>{money(v)}</div>
      </div>
    ))}
    <div className="bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{s.status}</div>
      <div className="text-sm font-bold uppercase" data-testid="inv-pay-summary-status">{(inv.status || '').replace('_', ' ')}</div>
    </div>
  </div>
);

export const InvoicePaymentsDialog = ({ invoice, open, onOpenChange, isAdmin, onChanged, onDeleteInvoice }) => {
  const { language } = useLanguage();
  const s = L[language] || L.en;
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const axCfg = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
  const invId = invoice?.invoice_id;

  const load = useCallback(async () => {
    if (!invId) return;
    try {
      const r = await axios.get(`${API}/sales/invoices/${invId}/payments`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  }, [invId]);

  useEffect(() => { if (open) { setData(null); setEditing(null); load(); } }, [open, load]);

  const applied = (res, msg) => { setData(res.data); setEditing(null); setConfirm(null); toast.success(msg); onChanged && onChanged(); };
  const save = async (row, f) => {
    try { applied(await axios.put(`${API}/sales/invoices/${invId}/payments/${row.row_id}`, { ...f, amount: parseFloat(f.amount) }, axCfg), s.saved); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const remove = async (row) => {
    try { applied(await axios.delete(`${API}/sales/invoices/${invId}/payments/${row.row_id}`, axCfg), s.removed); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); setConfirm(null); }
  };

  const inv = data?.invoice;
  const canDeleteInvoice = isAdmin && inv && (data.payments || []).length === 0 && inv.paid_amount <= 0.01;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-5xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="inv-payments-dialog">
        <DialogHeader><DialogTitle>{s.title} — {invoice?.invoice_no}</DialogTitle></DialogHeader>
        {!inv ? <div className="py-8 text-center text-sm text-zinc-500" data-testid="inv-pay-loading">…</div> : (
          <>
            <Summary inv={inv} s={s} />
            <div className="border border-zinc-200 overflow-x-auto">
              <table className="w-full text-left" data-testid="inv-pay-table">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
                  <tr>{['date', 'amount', 'method', 'reference', 'origin', 'notes', 'actions'].map((k) => <th key={k} className="px-2 py-2 font-semibold">{s[k]}</th>)}</tr>
                </thead>
                <tbody>
                  {data.payments.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-xs text-zinc-500" data-testid="inv-pay-empty">{s.none}</td></tr>}
                  {data.payments.map((row) => (editing === row.row_id
                    ? <EditRow key={row.row_id} row={row} s={s} onSave={(f) => save(row, f)} onCancel={() => setEditing(null)} />
                    : <PayRow key={row.row_id} row={row} s={s} isAdmin={isAdmin} onEdit={() => setEditing(row.row_id)} onRemove={() => setConfirm(row)} />))}
                </tbody>
                {data.payments.length > 0 && (
                  <tfoot className="bg-zinc-50 border-t border-zinc-200"><tr>
                    <td className="px-2 py-2 text-[10px] uppercase tracking-wider text-zinc-500">{s.paid}</td>
                    <td className="px-2 py-2 text-xs font-mono font-bold" data-testid="inv-pay-total-paid">{money(data.total_paid)}</td>
                    <td colSpan={5} />
                  </tr></tfoot>
                )}
              </table>
            </div>
            <div className="flex items-center justify-between gap-3 mt-4">
              <div className="text-xs text-zinc-500">{!canDeleteInvoice && isAdmin ? s.deleteInvoiceHint : ''}</div>
              {isAdmin && (
                <button disabled={!canDeleteInvoice} onClick={() => onDeleteInvoice && onDeleteInvoice(invoice)} className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="inv-pay-delete-invoice"><Trash size={12} />{s.deleteInvoice}</button>
              )}
            </div>
            {data.audit?.length > 0 && (
              <details className="mt-4 border border-zinc-200" data-testid="inv-pay-audit">
                <summary className="px-3 py-2 text-xs font-semibold cursor-pointer bg-zinc-50">{s.audit} ({data.audit.length})</summary>
                <ul className="divide-y divide-zinc-100">
                  {data.audit.map((a) => (
                    <li key={a.audit_id} className="px-3 py-1.5 text-[11px] text-zinc-600 font-mono">
                      {(a.at || '').slice(0, 16).replace('T', ' ')} · {a.by} · <b className="uppercase">{a.action}</b> · {money(a.before?.amount)}{a.after ? ` → ${money(a.after.amount)}` : ''} ({a.before?.payment_no || a.before?.reference || a.entity_id})
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
        <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
          <AlertDialogContent className="rounded-none" data-testid="inv-pay-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{confirm?.action === 'unlink' ? s.confirmUnlink : s.confirmDel} — {money(confirm?.amount)}</AlertDialogTitle>
              <AlertDialogDescription>{confirm?.action === 'unlink' ? s.unlinkDesc : s.delDesc}{confirm?.multi_invoice ? ` ${s.multi}.` : ''}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="inv-pay-confirm-cancel">{s.cancel}</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); remove(confirm); }} className="rounded-none bg-red-600 hover:bg-red-700" data-testid="inv-pay-confirm-ok">{confirm?.action === 'unlink' ? s.unlink : s.del}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};
